import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Play,
  Pause,
  Square,
  Plus,
  Trash2,
  Bell,
  Settings2,
  Check,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { WheelPicker } from "@/components/ui/WheelPicker";
import { cn } from "@/lib/utils";
import { useMeditationPresets, type MeditationPreset } from "@/hooks/useMeditationPresets";
import { usePractice } from "@/hooks/usePractice";

// ---------------------------------------------------------------------------
// Duration / interval options
// ---------------------------------------------------------------------------

const DURATION_VALUES = [1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 90, 105, 120];
const INTERVAL_VALUES = [0, 1, 2, 3, 5, 10, 15, 20];

// ---------------------------------------------------------------------------
// Web Audio: Tibetan Bowl Bell + Wooden Block
// ---------------------------------------------------------------------------

function playBell(ctx: AudioContext, type: "start" | "end" | "interval") {
  const freq = type === "start" ? 432 : type === "end" ? 528 : 396;
  const duration = type === "end" ? 4 : 2.5;

  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();
  const osc2 = ctx.createOscillator();
  const gainNode2 = ctx.createGain();

  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.97, ctx.currentTime + duration);
  gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

  osc2.type = "sine";
  osc2.frequency.setValueAtTime(freq * 2.76, ctx.currentTime);
  gainNode2.gain.setValueAtTime(0.08, ctx.currentTime);
  gainNode2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

  osc.connect(gainNode);
  gainNode.connect(ctx.destination);
  osc2.connect(gainNode2);
  gainNode2.connect(ctx.destination);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
  osc2.start(ctx.currentTime);
  osc2.stop(ctx.currentTime + duration);
}

function playWoodenBlock(ctx: AudioContext) {
  const bufferSize = ctx.sampleRate * 0.12;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 8);
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 800;
  filter.Q.value = 3;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.5, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start();
}

// ---------------------------------------------------------------------------
// Format helper
// ---------------------------------------------------------------------------

function fmtTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Preset Edit Sheet — with swipe-to-close + WheelPicker
// ---------------------------------------------------------------------------

interface PresetEditProps {
  preset?: Partial<MeditationPreset>;
  presetsCount: number;
  onSave: (p: { name: string; durationMinutes: number; intervalMinutes: number }) => void;
  onClose: () => void;
}

function PresetEditSheet({ preset, presetsCount, onSave, onClose }: PresetEditProps) {
  const [name, setName] = useState(preset?.name ?? "");
  const [dur, setDur] = useState(
    DURATION_VALUES.includes(preset?.durationMinutes ?? 20)
      ? (preset?.durationMinutes ?? 20)
      : 20
  );
  const [intervalMin, setIntervalMin] = useState(
    INTERVAL_VALUES.includes(preset?.intervalMinutes ?? 0)
      ? (preset?.intervalMinutes ?? 0)
      : 0
  );

  const touchStartY = useRef(0);

  // Swipe-to-close only on the handle pill area
  function onHandleTouchStart(e: React.TouchEvent) {
    touchStartY.current = e.touches[0].clientY;
  }
  function onHandleTouchMove(e: React.TouchEvent) {
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 60) onClose();
  }

  function handleSave() {
    const finalName = name.trim() || `Preset ${presetsCount + 1}`;
    onSave({ name: finalName, durationMinutes: dur, intervalMinutes: intervalMin });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end" onClick={onClose}>
      {/* Sheet: nearly full height, flex-col */}
      <div
        className="w-full bg-white dark:bg-[#0D1117] rounded-t-2xl flex flex-col"
        style={{ height: "88vh", maxHeight: "88vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle — swipe zone */}
        <div
          className="flex-shrink-0 pt-3 pb-2 flex flex-col items-center cursor-grab"
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
        >
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 space-y-5 pb-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Neues Preset</h3>

          {/* Name */}
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-muted-foreground">
              Name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`Preset ${presetsCount + 1}`}
              className="w-full h-10 rounded-lg border border-gray-200 dark:border-[#1E3A5F] bg-white dark:bg-[#0f1923] px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-vajra-cyan/50"
            />
          </div>

          {/* Dauer + Intervall */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs text-gray-500 dark:text-muted-foreground text-center block">
                Dauer (min)
              </label>
              <div className="rounded-xl border border-gray-200 dark:border-[#1E3A5F] overflow-hidden">
                <WheelPicker
                  values={DURATION_VALUES}
                  selected={dur}
                  onChange={setDur}
                  suffix="min"
                  accentClass="text-vajra-cyan"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-gray-500 dark:text-muted-foreground text-center block">
                Intervall (min)
              </label>
              <div className="rounded-xl border border-gray-200 dark:border-[#1E3A5F] overflow-hidden">
                <WheelPicker
                  values={INTERVAL_VALUES}
                  selected={intervalMin}
                  onChange={setIntervalMin}
                  suffix={intervalMin === 0 ? "aus" : "min"}
                  accentClass="text-vajra-cyan"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fixed save button at bottom */}
        <div className="flex-shrink-0 px-4 pb-8 pt-3 border-t border-gray-100 dark:border-[#1E3A5F]">
          <button
            type="button"
            onClick={handleSave}
            className="w-full h-12 rounded-xl bg-vajra-cyan text-white font-semibold text-sm hover:bg-vajra-cyan/90 transition-colors"
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function MeditationTimerPage() {
  const navigate = useNavigate();
  const { presets, addPreset, removePreset } = useMeditationPresets();
  const { addEntry, currentTimeSlot } = usePractice();

  // Timer state
  const [selected, setSelected] = useState<MeditationPreset | null>(presets[0] ?? null);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [saved, setSaved] = useState(false);

  // UI state
  const [showPresets, setShowPresets] = useState(false);
  const [editPreset, setEditPreset] = useState<{ preset?: Partial<MeditationPreset> } | null>(null);

  // Refs
  const audioCtxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastIntervalSecRef = useRef(0);
  const elapsedRef = useRef(0);

  function getAudioCtx(): AudioContext {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }

  // When a preset is selected, reset timer
  useEffect(() => {
    if (selected) {
      setRemaining(selected.durationMinutes * 60);
      elapsedRef.current = 0;
      lastIntervalSecRef.current = 0;
      setRunning(false);
      setFinished(false);
      setSaved(false);
    }
  }, [selected]);

  const handleStart = useCallback(() => {
    const ctx = getAudioCtx();
    playBell(ctx, "start");
    setRunning(true);
    setFinished(false);
    setSaved(false);
  }, []);

  const handlePause = useCallback(() => {
    setRunning(false);
  }, []);

  const handleStop = useCallback(() => {
    setRunning(false);
    if (selected) {
      setRemaining(selected.durationMinutes * 60);
      elapsedRef.current = 0;
      lastIntervalSecRef.current = 0;
    }
    setFinished(false);
    setSaved(false);
  }, [selected]);

  // Tick
  useEffect(() => {
    if (!running) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      elapsedRef.current += 1;

      // Interval bell?
      if (selected && selected.intervalMinutes > 0) {
        const intervalSec = selected.intervalMinutes * 60;
        if (elapsedRef.current - lastIntervalSecRef.current >= intervalSec) {
          lastIntervalSecRef.current = elapsedRef.current;
          const ctx = getAudioCtx();
          playWoodenBlock(ctx);
        }
      }

      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          setRunning(false);
          setFinished(true);
          const ctx = getAudioCtx();
          playBell(ctx, "end");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, selected]);

  // Direct save (without notes)
  const handleQuickSave = useCallback(async () => {
    if (!selected) return;
    const d = new Date();
    const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    await addEntry({
      date: localDate,
      timeSlot: currentTimeSlot.slot,
      type: "meditation",
      text: "",
      widmung: "",
      versprechen: "",
      meditationDauer: selected.durationMinutes,
      meditationArt: "Meditation",
    });
    setSaved(true);
  }, [selected, addEntry, currentTimeSlot, presets]);

  // Navigate to entry form for notes
  function handleDetailSave() {
    navigate("/entry", {
      state: {
        type: "meditation",
        meditationDauer: selected?.durationMinutes,
        meditationArt: selected?.name,
      },
    });
  }

  // Progress (0–1)
  const totalSec = selected ? selected.durationMinutes * 60 : 1;
  const progress = selected ? (totalSec - remaining) / totalSec : 0;
  const circumference = 2 * Math.PI * 90;

  return (
    <div className="px-4 py-6 space-y-5">
      {/* Back nav */}
      <button
        type="button"
        onClick={() => navigate("/")}
        className="flex items-center gap-1 text-sm text-vajra-cyan hover:text-vajra-cyan/80 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Zurück
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🧘</span>
          <h2 className="text-2xl font-bold tracking-tight">Meditation</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowPresets((v) => !v)}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
            showPresets
              ? "bg-vajra-cyan/20 text-vajra-cyan"
              : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          )}
          aria-label="Presets"
        >
          <Settings2 className="h-4 w-4" />
        </button>
      </div>

      {/* Presets management panel */}
      {showPresets ? (
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-4 space-y-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-muted-foreground">
                Presets ({presets.length}/10)
              </p>
              {presets.length < 10 && (
                <button
                  type="button"
                  onClick={() => setEditPreset({})}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-vajra-cyan hover:bg-vajra-cyan/10"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {presets.map((p) => (
              <div
                key={p.id}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-colors cursor-pointer",
                  selected?.id === p.id
                    ? "border-vajra-cyan/40 bg-vajra-cyan/8 dark:bg-vajra-cyan/10"
                    : "border-gray-200 dark:border-[#1E3A5F] hover:bg-gray-50 dark:hover:bg-[#1E3A5F]/20"
                )}
                onClick={() => { setSelected(p); setShowPresets(false); }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{p.name}</p>
                  <p className="text-xs text-gray-400 dark:text-muted-foreground">
                    {p.durationMinutes} min
                    {p.intervalMinutes > 0 ? ` · Intervall ${p.intervalMinutes} min` : ""}
                  </p>
                </div>
                {selected?.id === p.id && (
                  <span className="w-1.5 h-1.5 rounded-full bg-vajra-cyan shrink-0" />
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removePreset(p.id); }}
                  className="p-1 text-gray-300 hover:text-rose-400 transition-colors shrink-0"
                  aria-label="Preset löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Preset chips */}
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p)}
                disabled={running}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                  selected?.id === p.id
                    ? "border-vajra-cyan/60 bg-vajra-cyan/15 text-vajra-cyan"
                    : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground hover:border-vajra-cyan/30 disabled:opacity-40"
                )}
              >
                {p.name} · {p.durationMinutes} min
              </button>
            ))}
          </div>

          {/* Timer ring */}
          {selected && (
            <div className="flex flex-col items-center gap-6 py-4">
              <div className="relative">
                <svg width="220" height="220" className="-rotate-90">
                  <circle
                    cx="110" cy="110" r="90"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    className="text-gray-100 dark:text-[#1E3A5F]/60"
                  />
                  <circle
                    cx="110" cy="110" r="90"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    strokeLinecap="round"
                    className={saved ? "text-emerald-500" : finished ? "text-emerald-500" : "text-vajra-cyan"}
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - progress)}
                    style={{ transition: "stroke-dashoffset 0.9s linear" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                  {saved ? (
                    <>
                      <Check className="h-10 w-10 text-emerald-500" />
                      <p className="text-sm font-semibold text-emerald-500">Gespeichert!</p>
                    </>
                  ) : finished ? (
                    <>
                      <span className="text-3xl">🔔</span>
                      <p className="text-sm font-semibold text-emerald-500">Fertig</p>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold tabular-nums text-gray-900 dark:text-white tracking-tight">
                        {fmtTime(remaining)}
                      </span>
                      {selected.intervalMinutes > 0 && (
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Bell className="h-3 w-3" />
                          alle {selected.intervalMinutes} min
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Controls */}
              {!finished && !saved ? (
                <div className="flex items-center gap-4">
                  {running ? (
                    <>
                      <button
                        type="button"
                        onClick={handlePause}
                        className="flex h-14 w-14 items-center justify-center rounded-full bg-vajra-cyan/15 text-vajra-cyan hover:bg-vajra-cyan/20 transition-colors"
                      >
                        <Pause className="h-7 w-7" />
                      </button>
                      <button
                        type="button"
                        onClick={handleStop}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-[#1E3A5F]/60 text-gray-400 hover:text-rose-400 transition-colors"
                      >
                        <Square className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={handleStart}
                      className="flex h-16 w-16 items-center justify-center rounded-full bg-vajra-cyan text-white hover:bg-vajra-cyan/90 active:scale-95 transition-all shadow-lg shadow-vajra-cyan/20"
                    >
                      <Play className="h-8 w-8 ml-1" />
                    </button>
                  )}
                </div>
              ) : finished && !saved ? (
                <div className="flex flex-col items-center gap-3 w-full">
                  {/* Quick save */}
                  <button
                    type="button"
                    onClick={handleQuickSave}
                    className="w-full h-12 rounded-xl bg-vajra-cyan text-white font-semibold text-sm hover:bg-vajra-cyan/90 transition-colors"
                  >
                    Speichern
                  </button>
                  {/* Save with notes */}
                  <button
                    type="button"
                    onClick={handleDetailSave}
                    className="text-sm text-vajra-cyan hover:text-vajra-cyan/80 transition-colors"
                  >
                    Mit Notizen speichern →
                  </button>
                  <button
                    type="button"
                    onClick={handleStop}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    Ohne Eintrag schließen
                  </button>
                </div>
              ) : saved ? (
                <div className="flex flex-col items-center gap-3 w-full">
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="w-full h-12 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors"
                  >
                    Zurück zur Startseite
                  </button>
                  <button
                    type="button"
                    onClick={handleStop}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Neue Meditation
                  </button>
                </div>
              ) : null}

              {/* Preset info when idle */}
              {!running && !finished && !saved && (
                <p className="text-xs text-gray-400 dark:text-muted-foreground text-center">
                  {selected.name} · {selected.durationMinutes} Minuten
                  {selected.intervalMinutes > 0
                    ? ` · Gong alle ${selected.intervalMinutes} min`
                    : ""}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Preset Edit Sheet */}
      {editPreset !== null && (
        <PresetEditSheet
          preset={editPreset.preset}
          presetsCount={presets.length}
          onSave={(p) => addPreset(p)}
          onClose={() => setEditPreset(null)}
        />
      )}
    </div>
  );
}
